import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Upload, FileText, Image as ImageIcon, Loader2, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AIBookAssistant from '../../components/AIBookAssistant';

const AddBook = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    boek_naam: '',
    skrywer_naam: '',
    genre: [],  // Now array for multiple genres
    ouderdomsgroep: '',
    blurb: '',
    book_type: 'Vertaling',  // Boek Tipe: Vertaling or Oorspronklik
    workflow: 'proofreading' // 'proofreading' or 'direct'
  });
  const [bookFile, setBookFile] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverMethod, setCoverMethod] = useState('upload'); // 'upload' or 'ai'
  const [blurbMethod, setBlurbMethod] = useState('manual'); // 'manual' or 'ai'
  const [extractedPreview, setExtractedPreview] = useState('');
  const [availableGenres, setAvailableGenres] = useState([]);
  const [newGenre, setNewGenre] = useState('');
  const [generatingCovers, setGeneratingCovers] = useState(false);
  const [generatedCovers, setGeneratedCovers] = useState([]);
  const [showCoverDialog, setShowCoverDialog] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  const ageGroups = ['0-5 jaar', '6-9 jaar', '10-12 jaar', '13-17 jaar', '18+ jaar'];
  const bookTypes = [
    { value: 'Vertaling', label: 'Vertaling (vertaalde publieke domein boek)' },
    { value: 'Oorspronklik', label: 'Oorspronklik (skrywer se eie werk)' }
  ];

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/genres`);
      // API returns array directly
      setAvailableGenres(response.data || []);
    } catch (error) {
      console.error('Failed to fetch genres:', error);
      // Fallback genres
      setAvailableGenres(['Fiksie', 'Nie-fiksie', 'Romanties', 'Spanningsroman', 'Fantasie']);
    }
  };

  // Check for duplicate book when name or author changes
  const checkDuplicate = useCallback(async (boekNaam, skrywerNaam) => {
    if (!boekNaam?.trim() || !skrywerNaam?.trim()) {
      setDuplicateWarning(null);
      return;
    }
    
    setCheckingDuplicate(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('boek_naam', boekNaam.trim());
      formDataToSend.append('skrywer_naam', skrywerNaam.trim());
      
      const response = await axios.post(`${API}/check-duplicate-book`, formDataToSend);
      
      if (response.data.is_duplicate) {
        // is_resubmit means the writer's own book is in 'Wag vir Regmaak' — let them proceed
        if (response.data.is_resubmit) {
          setDuplicateWarning(null);
        } else {
          setDuplicateWarning(response.data.message);
        }
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      console.error('Failed to check duplicate:', error);
      setDuplicateWarning(null);
    } finally {
      setCheckingDuplicate(false);
    }
  }, []);

  // Debounced duplicate check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.boek_naam && formData.skrywer_naam) {
        checkDuplicate(formData.boek_naam, formData.skrywer_naam);
      }
    }, 500); // Wait 500ms after typing stops
    
    return () => clearTimeout(timer);
  }, [formData.boek_naam, formData.skrywer_naam, checkDuplicate]);

  const toggleGenre = (genre) => {
    if (formData.genre.includes(genre)) {
      setFormData({
        ...formData,
        genre: formData.genre.filter(g => g !== genre)
      });
    } else {
      setFormData({
        ...formData,
        genre: [...formData.genre, genre]
      });
    }
  };

  const addNewGenre = () => {
    if (newGenre.trim() && !availableGenres.includes(newGenre.trim())) {
      const trimmedGenre = newGenre.trim();
      setAvailableGenres([...availableGenres, trimmedGenre].sort());
      setFormData({
        ...formData,
        genre: [...formData.genre, trimmedGenre]
      });
      setNewGenre('');
      toast.success(`Genre "${trimmedGenre}" bygevoeg!`);
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image/(jpeg|jpg|png)')) {
        toast.error('Voorblad moet .JPG of .PNG formaat wees');
        return;
      }
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setCoverPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const generateAICovers = async () => {
    if (!bookFile) {
      toast.error('Laai eers die boek lêer op');
      return;
    }
    
    if (!formData.boek_naam || !formData.skrywer_naam || formData.genre.length === 0) {
      toast.error('Vul eers boek naam, skrywer naam en genre in');
      return;
    }

    setGeneratingCovers(true);
    
    try {
      const formDataToSend = new FormData();
      // Use extractedPreview if available, otherwise send a fallback message
      const previewText = extractedPreview || `'n Boek genaamd "${formData.boek_naam}" deur ${formData.skrywer_naam} in die ${formData.genre.join(', ')} genre.`;
      formDataToSend.append('preview_text', previewText);
      formDataToSend.append('boek_naam', formData.boek_naam);
      formDataToSend.append('skrywer_naam', formData.skrywer_naam);
      formDataToSend.append('genre', formData.genre.join(', '));

      const response = await axios.post(`${API}/generate-ai-covers`, formDataToSend);
      
      setGeneratedCovers(response.data.covers);
      setShowCoverDialog(true);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie voorblaaie genereer nie');
    } finally {
      setGeneratingCovers(false);
    }
  };

  const selectAICover = (coverBase64) => {
    // Convert base64 to blob and then to file
    const byteString = atob(coverBase64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: 'image/png' });
    const file = new File([blob], 'ai-generated-cover.png', { type: 'image/png' });
    
    setCoverImage(file);
    setCoverPreview(`data:image/png;base64,${coverBase64}`);
    setShowCoverDialog(false);
    toast.success('AI voorblad gekies!');
  };

  const handleBookFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (ext !== 'pdf' && ext !== 'docx' && ext !== 'epub') {
        toast.error('Boek moet .PDF, .DOCX of .EPUB formaat wees');
        return;
      }
      setBookFile(file);
      
      // Always extract preview when book file is uploaded
      await extractPreviewFromFile(file);
    }
  };

  const extractPreviewFromFile = async (file) => {
    toast.info('Besig om boek inhoud te verwerk... ⏳');
    
    const formData = new FormData();
    formData.append('book_file', file);
    
    try {
      const response = await axios.post(`${API}/extract-book-preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setExtractedPreview(response.data.preview_text);
      toast.success(`Boek lêer verwerk! ${response.data.word_count.toLocaleString()} woorde gevind. AI funksies is nou beskikbaar.`);
    } catch (error) {
      console.error('Preview extraction error:', error);
      // Extract error message properly
      const errorMsg = error.response?.data?.detail || error.message || 'Kon nie boek inhoud verwerk nie';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Kon nie boek inhoud verwerk nie. Probeer weer.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;
    
    // Check for duplicate warning
    if (duplicateWarning) {
      toast.error(duplicateWarning);
      return;
    }
    
    if (!bookFile || !coverImage) {
      toast.error('Beide boek file en voorblad is vereis');
      return;
    }

    setLoading(true);

    if (formData.genre.length === 0) {
      toast.error('Kies asseblief ten minste een genre');
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append('boek_naam', formData.boek_naam);
    data.append('skrywer_naam', formData.skrywer_naam);
    data.append('genre', JSON.stringify(formData.genre));  // Send as JSON array
    data.append('ouderdomsgroep', formData.ouderdomsgroep);
    data.append('blurb', formData.blurb);
    data.append('book_file', bookFile);
    data.append('cover_image', coverImage);
    data.append('book_type', formData.book_type);
    data.append('skip_proofreading', formData.workflow === 'direct');

    try {
      const response = await axios.post(`${API}/admin/books/add`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (formData.workflow === 'direct') {
        toast.success('Boek suksesvol bygevoeg en gereed vir publikasie!');
      } else {
        toast.success('Boek suksesvol bygevoeg en gestuur vir proeflees!');
      }
      
      navigate('/admin/books');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie boek byvoeg nie');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Voeg Boek By Sisteem
        </h2>
        <p className="text-gray-600">Laai 'n boek op direk na die sisteem</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Boek Inligting</CardTitle>
          <CardDescription>Verskaf al die nodige inligting oor die boek</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" onKeyDown={(e) => {
            // Prevent Enter key from submitting the form in input fields
            if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.type !== 'submit') {
              e.preventDefault();
            }
          }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="boek_naam">Boek Naam *</Label>
                <Input
                  id="boek_naam"
                  data-testid="admin-book-name-input"
                  required
                  value={formData.boek_naam}
                  onChange={(e) => setFormData({ ...formData, boek_naam: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="skrywer_naam">Skrywer Naam *</Label>
                <Input
                  id="skrywer_naam"
                  data-testid="admin-author-name-input"
                  required
                  value={formData.skrywer_naam}
                  onChange={(e) => setFormData({ ...formData, skrywer_naam: e.target.value })}
                />
              </div>

              {/* Duplicate Warning */}
              {(duplicateWarning || checkingDuplicate) && (
                <div className={`col-span-2 p-3 rounded-lg flex items-center gap-2 ${
                  duplicateWarning ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                }`}>
                  {checkingDuplicate ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                      <span className="text-sm text-gray-600">Kontroleer vir duplikate...</span>
                    </>
                  ) : duplicateWarning ? (
                    <>
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <span className="text-sm text-red-700">{duplicateWarning}</span>
                    </>
                  ) : null}
                </div>
              )}

              {/* Book Type Selection */}
              <div className="space-y-2 col-span-2">
                <Label htmlFor="book_type">Boek Tipe *</Label>
                <RadioGroup 
                  value={formData.book_type} 
                  onValueChange={(value) => setFormData({ ...formData, book_type: value })}
                  className="flex flex-col space-y-2"
                >
                  {bookTypes.map((type) => (
                    <div key={type.value} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                      <RadioGroupItem value={type.value} id={`book_type_${type.value}`} />
                      <Label htmlFor={`book_type_${type.value}`} className="cursor-pointer flex-1">
                        <span className="font-medium">{type.value}</span>
                        <span className="text-gray-500 ml-2 text-sm">({type.label.split('(')[1]}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label htmlFor="genre">Genre (kies een of meer) *</Label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-2">
                    {availableGenres.map((genre) => (
                      <div
                        key={genre}
                        onClick={() => toggleGenre(genre)}
                        className={`cursor-pointer px-3 py-2 rounded-md text-sm transition-colors ${
                          formData.genre.includes(genre)
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                        }`}
                      >
                        {genre}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Voeg nuwe genre by..."
                    value={newGenre}
                    onChange={(e) => setNewGenre(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addNewGenre()}
                    className="flex-1"
                  />
                  <Button type="button" onClick={addNewGenre} variant="outline">
                    Voeg By
                  </Button>
                </div>
                {formData.genre.length > 0 && (
                  <p className="text-sm text-gray-600">
                    Gekies: {formData.genre.join(', ')}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ouderdomsgroep">Ouderdomsgroep *</Label>
                <Select
                  value={formData.ouderdomsgroep}
                  onValueChange={(value) => setFormData({ ...formData, ouderdomsgroep: value })}
                  required
                >
                  <SelectTrigger data-testid="admin-age-group-select">
                    <SelectValue placeholder="Kies ouderdomsgroep" />
                  </SelectTrigger>
                  <SelectContent>
                    {ageGroups.map((group) => (
                      <SelectItem key={group} value={group}>
                        {group}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Blurb Metode *</Label>
                <RadioGroup value={blurbMethod} onValueChange={setBlurbMethod}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="manual" id="manual-blurb" />
                    <Label htmlFor="manual-blurb" className="cursor-pointer">Skryf eie blurb</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ai" id="ai-blurb" />
                    <Label htmlFor="ai-blurb" className="cursor-pointer flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-purple-500" />
                      AI genereer professionele blurb
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {blurbMethod === 'manual' ? (
                <div className="space-y-2">
                  <Label htmlFor="blurb">Blurb (Agterblad Beskrywing) *</Label>
                  <Textarea
                    id="blurb"
                    data-testid="admin-blurb-input"
                    required
                    rows={5}
                    placeholder="Beskryf die boek in 100-500 karakters..."
                    value={formData.blurb}
                    onChange={(e) => setFormData({ ...formData, blurb: e.target.value })}
                  />
                  <p className="text-sm text-gray-500">{formData.blurb.length} karakters</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {!bookFile || !extractedPreview ? (
                    <div className="p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
                      <p className="text-sm text-purple-900">
                        ℹ️  Laai eers die boek lêer op. AI sal dan 'n professionele blurb genereer gebaseer op die boek se inhoud.
                      </p>
                    </div>
                  ) : (
                    <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        ✅ Boek lêer gelaai! Gebruik die AI Assistent hieronder om 'n blurb te genereer.
                      </p>
                    </div>
                  )}
                  {formData.blurb && (
                    <div>
                      <Label>AI Gegenereerde Blurb:</Label>
                      <Textarea
                        value={formData.blurb}
                        onChange={(e) => setFormData({ ...formData, blurb: e.target.value })}
                        rows={5}
                        className="mt-2"
                      />
                      <p className="text-sm text-gray-500 mt-1">{formData.blurb.length} karakters</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="book_file">Boek File (.PDF, .DOCX of .EPUB) *</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-orange-400 transition">
                  <input
                    id="book_file"
                    data-testid="admin-book-file-input"
                    type="file"
                    accept=".pdf,.docx,.epub,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/epub+zip"
                    onChange={handleBookFileChange}
                    className="hidden"
                  />
                  <label htmlFor="book_file" className="cursor-pointer flex flex-col items-center">
                    <FileText className="h-12 w-12 text-gray-400 mb-2" />
                    {bookFile ? (
                      <span className="text-sm text-gray-900 font-semibold">{bookFile.name}</span>
                    ) : (
                      <>
                        <span className="text-sm text-gray-600">Klik om boek op te laai</span>
                        <span className="text-xs text-gray-400">PDF, DOCX of EPUB (Maks 50MB)</span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <Label>Voorblad Metode *</Label>
                <RadioGroup value={coverMethod} onValueChange={setCoverMethod}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="upload" id="upload" />
                    <Label htmlFor="upload" className="cursor-pointer">Laai eie voorblad op</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ai" id="ai" />
                    <Label htmlFor="ai" className="cursor-pointer">Genereer AI voorblad</Label>
                  </div>
                </RadioGroup>

                {coverMethod === 'upload' ? (
                  <div className="space-y-2">
                    <Label htmlFor="cover_image">Voorblad (Aanbeveel: 1024 × 1536 pixels) *</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-orange-400 transition">
                      <input
                        id="cover_image"
                        data-testid="admin-cover-image-input"
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={handleCoverChange}
                        className="hidden"
                      />
                      <label htmlFor="cover_image" className="cursor-pointer flex flex-col items-center">
                        {coverPreview ? (
                          <img src={coverPreview} alt="Cover preview" className="h-32 w-auto object-contain mb-2 rounded" />
                        ) : (
                          <ImageIcon className="h-12 w-12 text-gray-400 mb-2" />
                        )}
                        <span className="text-sm text-gray-600">Klik om voorblad op te laai</span>
                        <span className="text-xs text-gray-400">JPG of PNG - Aanbeveel 1024×1536</span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {!bookFile ? (
                      <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-900">
                          ℹ️  Laai eers die boek lêer op. AI sal dan voorblaaie genereer gebaseer op die boek se inhoud.
                        </p>
                      </div>
                    ) : !extractedPreview ? (
                      <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-900">
                          ⚠️ Boek lêer gelaai, maar inhoud kon nie geëkstraheer word nie. Gebruik die AI Assistent hieronder om tog te probeer, of laai 'n ander lêer formaat op.
                        </p>
                      </div>
                    ) : (
                      <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          ✅ Boek lêer gelaai! Gebruik die AI Assistent hieronder om voorblaaie te genereer.
                        </p>
                      </div>
                    )}
                      <div className="space-y-3">
                        <Button
                          type="button"
                          onClick={generateAICovers}
                          disabled={generatingCovers}
                          className="w-full bg-green-600 hover:bg-green-700"
                        >
                          {generatingCovers ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Genereer 3 AI Voorblaaie... (~60 sekondes)
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Genereer 3 AI Voorblaaie
                            </>
                          )}
                        </Button>
                        
                        {showCoverDialog && generatedCovers.length > 0 && (
                          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                                <Sparkles className="h-5 w-5 text-green-600" />
                                Kies AI Voorblad
                              </h3>
                              <div className="grid grid-cols-3 gap-4 mb-4">
                                {generatedCovers.map((cover) => (
                                  <div key={cover.id} className="space-y-2">
                                    <div className="border-2 border-gray-200 rounded-lg overflow-hidden hover:border-green-500 transition-colors">
                                      <img 
                                        src={`data:image/png;base64,${cover.image_base64}`}
                                        alt={`Opsie ${cover.id + 1}`}
                                        className="w-full h-auto cursor-pointer"
                                        onClick={() => selectAICover(cover.image_base64)}
                                      />
                                    </div>
                                    <Button
                                      size="sm"
                                      className="w-full bg-green-600 hover:bg-green-700"
                                      onClick={() => selectAICover(cover.image_base64)}
                                    >
                                      Gebruik Opsie {cover.id + 1}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                              <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => setShowCoverDialog(false)}
                              >
                                Kanselleer
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                  </div>
                )}

                {coverPreview && coverMethod === 'ai' && (
                  <div className="text-center">
                    <img src={coverPreview} alt="Selected AI cover" className="h-48 w-auto mx-auto object-contain rounded shadow-lg" />
                    <p className="text-sm text-green-600 font-medium mt-2">✓ AI voorblad gekies</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6">
              <Label className="text-base font-semibold mb-4 block">Kies Werkvloei *</Label>
              <RadioGroup
                value={formData.workflow}
                onValueChange={(value) => setFormData({ ...formData, workflow: value })}
              >
                <div className="flex items-start space-x-3 mb-4 p-4 bg-white rounded-lg border border-gray-200 hover:border-orange-300 transition">
                  <RadioGroupItem value="proofreading" id="proofreading" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="proofreading" className="cursor-pointer font-semibold text-gray-900 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-blue-500" />
                      Voeg by Proeflees Queue
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Boek sal deur die normale proeflees proses gaan
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-orange-300 transition">
                  <RadioGroupItem value="direct" id="direct" className="mt-1" />
                  <div className="flex-1">
                    <Label htmlFor="direct" className="cursor-pointer font-semibold text-gray-900 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-orange-500" />
                      Slaan Proeflees Oor
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Boek gaan direk na publikasie (Let wel: Geen proeflees nie)
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* AI Book Assistant - Shows when book file uploaded and AI methods selected */}
            {bookFile && (blurbMethod === 'ai' || coverMethod === 'ai') && (
              <AIBookAssistant
                boekNaam={formData.boek_naam}
                skrywerNaam={formData.skrywer_naam}
                genre={formData.genre === 'Ander' ? formData.custom_genre : formData.genre}
                previewText={extractedPreview}
                bookFile={bookFile}
                initialBlurb={formData.blurb}
                onBlurbGenerated={(blurb) => setFormData({ ...formData, blurb })}
                onCoverSelect={(file, preview) => {
                  setCoverImage(file);
                  setCoverPreview(preview);
                }}
              />
            )}

            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  // Confirm if user has entered any data
                  const hasData = formData.boek_naam || formData.skrywer_naam || bookFile || coverImage;
                  if (hasData) {
                    if (window.confirm('Is jy seker jy wil kanselleer? Alle inligting sal verlore gaan.')) {
                      navigate('/admin/books');
                    }
                  } else {
                    navigate('/admin/books');
                  }
                }}
              >
                Kanselleer
              </Button>
              <Button
                data-testid="admin-submit-book-btn"
                type="submit"
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 py-6 text-lg disabled:opacity-50"
                disabled={loading || duplicateWarning}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Besig om by te voeg...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-5 w-5" />
                    Voeg Boek By
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddBook;
